import {
  ArrowRightLeft,
  BarChart3,
  BookOpen,
  FolderOpen,
  Megaphone,
  Palette,
  PanelBottom,
  PanelTop,
  Search,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import CloudflareLogo from "~/assets/cloudflare-logo.svg";
import VercelLogo from "~/assets/vercel-logo.svg";

export type FoundryHostingProvider = "cloudflare" | "vercel";

export type FoundryControlPanelItem = {
  title: string;
  path: string;
  icon?: LucideIcon;
  logo?: string;
  description: string;
};

export type FoundryControlPanelGroup = {
  title: string;
  items: FoundryControlPanelItem[];
};

export const FOUNDRY_CONTROL_PANEL_HUB = "/foundry/control-panel/general";

export function foundrySetupPath(provider: FoundryHostingProvider) {
  return `/foundry/control-panel/setup/${provider}`;
}

const GENERAL_ITEM: FoundryControlPanelItem = {
  title: "General",
  path: "/foundry/control-panel/general",
  icon: SlidersHorizontal,
  description: "Turn Foundry on and choose who sees new drafts.",
};

const ANALYTICS_ITEM: FoundryControlPanelItem = {
  title: "Analytics",
  path: "/foundry/control-panel/analytics",
  icon: BarChart3,
  description: "Tracking on published Foundry pages.",
};

const SEARCH_ENGINES_ITEM: FoundryControlPanelItem = {
  title: "Search engines",
  path: "/foundry/control-panel/search-engines",
  icon: Search,
  description: "Keep your Foundry sitemap submitted to Google and Bing.",
};

const REDIRECTS_ITEM: FoundryControlPanelItem = {
  title: "Redirects",
  path: "/foundry/control-panel/redirects",
  icon: ArrowRightLeft,
  description: "Send visitors from old URLs to new ones.",
};

const BRANDING_ITEM: FoundryControlPanelItem = {
  title: "Branding",
  path: "/foundry/control-panel/branding",
  icon: Palette,
  description: "Name, logo, colors, and custom CSS visitors see.",
};

const BLOG_ITEM: FoundryControlPanelItem = {
  title: "Blog",
  path: "/foundry/control-panel/blog",
  icon: BookOpen,
  description: "Reading layout, progress bar, and how posts appear.",
};

const HEADER_ITEM: FoundryControlPanelItem = {
  title: "Header",
  path: "/foundry/control-panel/header",
  icon: PanelTop,
  description: "The bar at the top of every page.",
};

const FOOTER_ITEM: FoundryControlPanelItem = {
  title: "Footer",
  path: "/foundry/control-panel/footer",
  icon: PanelBottom,
  description: "The bar at the bottom of every page.",
};

const AUTHORS_ITEM: FoundryControlPanelItem = {
  title: "Authors",
  path: "/foundry/control-panel/authors",
  icon: Users,
  description: "People credited on your published blog posts.",
};

const CATEGORIES_ITEM: FoundryControlPanelItem = {
  title: "Categories",
  path: "/foundry/control-panel/categories",
  icon: FolderOpen,
  description: "Topics that organize posts on your public blog.",
};

const CTA_ITEM: FoundryControlPanelItem = {
  title: "CTAs",
  path: "/foundry/control-panel/cta",
  icon: Megaphone,
  description: "Promo blocks you reuse on blogs and landing pages.",
};

const CLOUDFLARE_SETUP_ITEM: FoundryControlPanelItem = {
  title: "Cloudflare",
  path: foundrySetupPath("cloudflare"),
  logo: CloudflareLogo,
  description: "Connect Foundry through Cloudflare.",
};

const VERCEL_SETUP_ITEM: FoundryControlPanelItem = {
  title: "Vercel",
  path: foundrySetupPath("vercel"),
  logo: VercelLogo,
  description: "Connect Foundry through Vercel.",
};

const SETTINGS_GROUP: FoundryControlPanelGroup = {
  title: "Settings",
  items: [GENERAL_ITEM, ANALYTICS_ITEM, SEARCH_ENGINES_ITEM, REDIRECTS_ITEM],
};

const YOUR_SITE_GROUP: FoundryControlPanelGroup = {
  title: "Your site",
  items: [BRANDING_ITEM, BLOG_ITEM, HEADER_ITEM, FOOTER_ITEM],
};

const CONTENT_GROUP: FoundryControlPanelGroup = {
  title: "Content",
  items: [AUTHORS_ITEM, CATEGORIES_ITEM, CTA_ITEM],
};

const SETUP_CLOUDFLARE_GROUP: FoundryControlPanelGroup = {
  title: "Setup",
  items: [CLOUDFLARE_SETUP_ITEM],
};

const SETUP_VERCEL_GROUP: FoundryControlPanelGroup = {
  title: "Setup",
  items: [VERCEL_SETUP_ITEM],
};

const SETUP_BOTH_GROUP: FoundryControlPanelGroup = {
  title: "Setup",
  items: [CLOUDFLARE_SETUP_ITEM, VERCEL_SETUP_ITEM],
};

export function getFoundryControlPanelGroups(
  provider: FoundryHostingProvider | null
): FoundryControlPanelGroup[] {
  const setupGroup =
    provider === "vercel"
      ? SETUP_VERCEL_GROUP
      : provider === "cloudflare"
        ? SETUP_CLOUDFLARE_GROUP
        : SETUP_BOTH_GROUP;

  return [SETTINGS_GROUP, YOUR_SITE_GROUP, CONTENT_GROUP, setupGroup];
}

const ALL_ITEMS: FoundryControlPanelItem[] = [
  ...SETTINGS_GROUP.items,
  ...YOUR_SITE_GROUP.items,
  ...CONTENT_GROUP.items,
  CLOUDFLARE_SETUP_ITEM,
  VERCEL_SETUP_ITEM,
];

export function findFoundryControlPanelItem(
  pathname: string
): FoundryControlPanelItem | null {
  const exact = ALL_ITEMS.find((item) => item.path === pathname);
  if (exact) return exact;

  return (
    ALL_ITEMS.filter((item) => pathname.startsWith(`${item.path}/`)).sort(
      (a, b) => b.path.length - a.path.length
    )[0] ?? null
  );
}

export function findFoundryControlPanelGroup(
  pathname: string
): FoundryControlPanelGroup | null {
  const item = findFoundryControlPanelItem(pathname);
  if (!item) return null;
  if (item === CLOUDFLARE_SETUP_ITEM) return SETUP_CLOUDFLARE_GROUP;
  if (item === VERCEL_SETUP_ITEM) return SETUP_VERCEL_GROUP;
  return (
    [SETTINGS_GROUP, YOUR_SITE_GROUP, CONTENT_GROUP].find((group) =>
      group.items.includes(item)
    ) ?? null
  );
}
